// components
import { Button } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import EntitySearch, {
  EmptyQuerySearchOptions
} from 'components/mrp/components/EntitySearch';
import { Popover } from 'components/mrp/components/Popover';

// stores/context
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { DialogContext } from 'components/shared/DialogProvider/DialogContext';
import { DialogType } from 'components/shared/DialogProvider/DialogProvider.enums';
import { useCreateShortcutContext } from 'components/shared/ShortcutProvider';
import { ShortcutContext } from 'components/shared/ShortcutProvider/ShortcutContext';

// types
import type { PlanningEntityIdentifier } from 'generated/index';

// react/utils
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const AddAvailabilityButton = ({
  disabled = false
}: {
  disabled?: boolean;
}) => {
  'use memo';
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const [isOpenPopoverOpen, setIsPopoverOpen] = useState(false);

  const { setActiveShortcutContexts, shortcuts } = useContext(ShortcutContext);

  const calendars = useCalendarStore((state) => state.calendars);

  const { openDialog } = useContext(DialogContext);

  useCreateShortcutContext('toolbar-actions', [
    {
      name: 'addAvailability',
      label: t('shortcuts.addAvailability.label'),
      description: t('shortcuts.addAvailability.description'),
      callback: setIsPopoverOpen.bind(null, true),
      activatorKey: 'B',
      controllerKeys: ['PRIMARY']
    }
  ]);

  useEffect(() => {
    setActiveShortcutContexts?.(
      !!isOpenPopoverOpen ? [] : Object.keys(shortcuts)
    );
  }, [isOpenPopoverOpen, setActiveShortcutContexts, shortcuts]);

  // listen for shortcuts
  useEffect(() => {
    const handleKeyUp = (event: KeyboardEvent) => {
      // NOTE: close the add calendar when Escape is pressed
      if (event.code === 'Escape') {
        event.preventDefault();
        setIsPopoverOpen(false);
      }
    };

    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const emptyQueryOptions: EmptyQuerySearchOptions = {
    groupLabel: t('openedCalendars'),
    options: Object.values(calendars)
      .flatMap(({ sources }) => sources)
      .map((calendarData) => ({
        id: calendarData.planningEntityIdentifier.entityId!,
        label: calendarData.label,
        group: t(
          `entityTypeMap.${calendarData.planningEntityIdentifier.planningEntityType}.single`
        ),
        isLocal: true,
        entity: calendarData.planningEntityIdentifier
      }))
  };

  const onChange = (
    planningEntityIdentifier: PlanningEntityIdentifier,
    label: string
  ) => {
    // Open dialog
    openDialog?.(
      'entity-availability-dialog',
      DialogType.ENTITY_AVAILABILITY_DIALOG,
      {
        planningEntityIdentifier,
        entityLabel: label
      }
    );

    setIsPopoverOpen(false);
  };

  return (
    <Popover
      id="entity-search-popover"
      useForcedState={true}
      forceOpen={isOpenPopoverOpen}
      onClose={() => setIsPopoverOpen(false)}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom'
      }}
      component={
        <EntitySearch
          emptyQuerySearchOptions={emptyQueryOptions}
          entityTypeFilters={['CLIENT', 'RESOURCE']}
          minWidth={400}
          onChange={onChange}
        />
      }
    >
      <Button
        variant="outlined"
        onClick={() => setIsPopoverOpen(true)}
        startIcon={<Icon type="calendar-clock" />}
        disabled={disabled}
      >
        {t('addAvailability')}
      </Button>
    </Popover>
  );
};

export default AddAvailabilityButton;
